import type { Relatorio } from './api.ts';

/**
 * A busca, o filtro e a ordem da lista de Relatórios — PRD §73, 29/10/2026.
 *
 * > *"Precisamos colocar filtros, por exemplo, 'concluídos/entregues' e 'em
 * > andamento'. Bem como, conseguir pesquisar pelo nome ou número do
 * > relatório."* — Richard
 *
 * ⛔ **Roda só no navegador, e só sobre o que a linha já mostra:** o código do
 * relatório, o código do caso, o título do caso e o nome do cliente. Não vai ao
 * servidor nem à busca geral (Ctrl+K), e por isso não traz nome de investigado
 * que não esteja escrito no título — a trava do manual, investigado fora da
 * busca global, continua de pé.
 *
 * ⚠️ **Funções puras.** A tela guarda a escolha; aqui só se decide o que entra e
 * em que ordem. Regressão: `npm run testar-lista-de-relatorios` (em `api/`).
 */

export type FiltroDaLista = 'todos' | 'andamento' | 'entregues';
export type OrdemDaLista = 'numero-desc' | 'numero-asc' | 'devedor-az' | 'devedor-za';

export const FILTROS_DA_LISTA: FiltroDaLista[] = ['todos', 'andamento', 'entregues'];
export const ORDENS_DA_LISTA: OrdemDaLista[] = ['numero-desc', 'numero-asc', 'devedor-az', 'devedor-za'];

/** O que a regra lê de cada relatório — e nada além disso. */
export type LinhaDaLista = Pick<Relatorio, 'codigo' | 'situacao'> & {
  caso: Pick<Relatorio['caso'], 'codigo' | 'titulo'>;
  cliente: Pick<Relatorio['cliente'], 'nome'>;
};

/** Sem caixa e sem acento: "omega" acha "ÔMEGA". */
function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function soDigitos(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * ⚠️ **"Em andamento" é tudo que ainda não foi entregue**, e não só `rascunho`.
 * O tipo ainda carrega `fechado`, que a tela não oferece desde 20/08/2026; se
 * algum aparecer, ele não está entregue e não pode sumir dos dois filtros.
 */
function estaEntregue(r: LinhaDaLista): boolean {
  return r.situacao === 'entregue';
}

/**
 * Acha pelo código do relatório ("REL-2026-051", ou só "51"), pelo código do
 * caso, pelo devedor principal (o título do caso) e pelo cliente.
 *
 * ⭐ **O CNPJ escrito no título acha com ou sem pontos.** O título guarda o CNPJ
 * formatado e a mão digita só os números (ou o contrário). A comparação por
 * dígitos só entra quando o termo é número — senão "LTDA 2" casaria com
 * qualquer título que tivesse um 2.
 */
export function achaNaBusca(r: LinhaDaLista, busca: string): boolean {
  const termo = busca.trim();
  if (!termo) return true;
  const campos = [r.codigo, r.caso.codigo, r.caso.titulo ?? '', r.cliente.nome];
  const procurado = semAcento(termo);
  if (campos.some((c) => semAcento(c).includes(procurado))) return true;
  const digitos = soDigitos(termo);
  if (!digitos || !/^[\d\s./-]+$/.test(termo)) return false;
  return campos.some((c) => soDigitos(c).includes(digitos));
}

/**
 * ⛔ **O número de cada botão é o que o clique mostra** — a lição da fila do
 * dia, 30/09/2026. Por isso conta depois da busca, e não a lista inteira.
 */
export function contarPorFiltro(lista: LinhaDaLista[], busca: string): Record<FiltroDaLista, number> {
  const achados = lista.filter((r) => achaNaBusca(r, busca));
  const entregues = achados.filter(estaEntregue).length;
  return { todos: achados.length, andamento: achados.length - entregues, entregues };
}

/**
 * "REL-2026-051" → [2026, 51]. O ano manda e a sequência desempata: o
 * REL-2027-001 vem depois de todos os de 2026, o que a comparação de texto
 * também faria hoje — e deixaria de fazer no REL-2026-1000.
 *
 * Código fora do formato devolve nulo e vai para o fim, nas duas direções.
 */
function numeroDoCodigo(codigo: string): [number, number] | null {
  const m = /(\d{4})\D+(\d+)\s*$/.exec(codigo);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

const NOMES = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

function compararNumero(a: LinhaDaLista, b: LinhaDaLista): number {
  const na = numeroDoCodigo(a.codigo);
  const nb = numeroDoCodigo(b.codigo);
  if (!na || !nb) return na ? -1 : nb ? 1 : NOMES.compare(a.codigo, b.codigo);
  return na[0] - nb[0] || na[1] - nb[1];
}

/**
 * ⚠️ **O devedor principal é o título do caso** — o nome que a linha mostra, e o
 * mesmo que o e-mail de entrega usa (`rotas/relatorios.ts`). Caso sem título
 * vai para o fim nas duas ordens: "sem nome" não é a primeira letra de nada.
 */
function devedor(r: LinhaDaLista): string {
  return (r.caso.titulo ?? '').trim();
}

export function ordenarLista<T extends LinhaDaLista>(lista: T[], ordem: OrdemDaLista): T[] {
  const copia = [...lista];
  if (ordem === 'numero-asc') return copia.sort(compararNumero);
  if (ordem === 'numero-desc') {
    return copia.sort((a, b) => {
      /* O código fora do formato continua no fim: inverter a comparação o
         levaria para o topo. */
      const semNumero = Number(!numeroDoCodigo(a.codigo)) - Number(!numeroDoCodigo(b.codigo));
      return semNumero || compararNumero(b, a);
    });
  }
  const sentido = ordem === 'devedor-az' ? 1 : -1;
  return copia.sort((a, b) => {
    const da = devedor(a);
    const db = devedor(b);
    if (!da || !db) return Number(!da) - Number(!db) || compararNumero(b, a);
    /* Mesmo devedor em dois casos: o relatório mais novo primeiro. */
    return sentido * NOMES.compare(da, db) || compararNumero(b, a);
  });
}

/** A lista como a tela mostra: a busca, depois o filtro, depois a ordem. */
export function listaVisivel<T extends LinhaDaLista>(
  lista: T[],
  { filtro, busca, ordem }: { filtro: FiltroDaLista; busca: string; ordem: OrdemDaLista },
): T[] {
  const vistos = lista.filter(
    (r) =>
      achaNaBusca(r, busca) &&
      (filtro === 'todos' || (filtro === 'entregues') === estaEntregue(r)),
  );
  return ordenarLista(vistos, ordem);
}
